import { Logger } from '../logger';
import { localStore } from './safeStorage';

/**
 * App-wide localStorage keys and the single place that wipes storage.
 *
 * Most keys are per-feature and belong to the module that owns them. The ones
 * listed here are different: they are read by more than one module *and* they
 * have to survive a storage reset, so the reset rule and the key have to be
 * defined together.
 */
export const DurableStorageKeys = {
  /**
   * Stable per-device UUID sent as `x-device-id`. Identifies the physical
   * screen, not the user, so it outlives every reset including a real logout.
   */
  DEVICE_ID: 'pulsar-device-id',

  /** How the current user signed in (`email` | `okta` | `entra`). */
  AUTH_CHANNEL: 'PULSAR#AUTH_CHANNEL',

  /** Fingerprint of the last person-properties payload sent to PostHog. */
  ANALYTICS_IDENTITY: 'PULSAR#ANALYTICS_IDENTITY',

  /**
   * Local date (`YYYY-MM-DD`) this device last sent `[TVMode]opened`. Like the
   * device id it describes the screen, not the user, so it outlives every reset:
   * otherwise each re-login on a TV would re-send the day's event.
   */
  TV_MODE_OPENED_ON: 'PULSAR#TV_MODE_OPENED_ON'
} as const;

/** Keys that belong to the physical device and survive even a real logout. */
const DEVICE_KEYS: readonly string[] = [
  DurableStorageKeys.DEVICE_ID,
  DurableStorageKeys.TV_MODE_OPENED_ON
];

/**
 * PostHog keeps its distinct id and `$user_state` under these prefixes
 * (`ph_<token>_posthog`, `__ph_opt_in_out_<token>`). Matching by prefix rather
 * than by the exact key keeps this correct if the SDK adds keys or we ever set
 * `persistence_name`.
 */
const POSTHOG_KEY_PREFIXES = ['ph_', '__ph_'];

/**
 * Ours and PostHog's halves of the analytics identity. They must be kept or
 * dropped together: a preserved fingerprint next to a wiped PostHog distinct id
 * would suppress the `identify` call that re-establishes it.
 */
const ANALYTICS_IDENTITY_KEYS: readonly string[] = [
  DurableStorageKeys.AUTH_CHANNEL,
  DurableStorageKeys.ANALYTICS_IDENTITY
];

export interface ClearAppStorageOptions {
  /**
   * Whether the PostHog distinct id and the person-properties fingerprint
   * survive. Defaults to `true`.
   *
   * Pass `false` only for a deliberate end of session (logout, account
   * deletion, a rejected token). Everywhere else — most importantly the auth
   * gate, which also fires on transient unauthenticated renders — dropping the
   * identity makes PostHog treat the very same user as a brand new anonymous
   * visitor on the next load, emitting a fresh `$identify` and polluting person
   * merges with a throwaway anonymous id.
   */
  keepAnalyticsIdentity?: boolean;
}

const isDurable = (key: string, keepAnalyticsIdentity: boolean): boolean => {
  if (DEVICE_KEYS.includes(key)) return true;
  if (!keepAnalyticsIdentity) return false;
  return (
    ANALYTICS_IDENTITY_KEYS.includes(key) ||
    POSTHOG_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))
  );
};

/**
 * Resets the app's localStorage, restoring the keys that must outlive it.
 *
 * Use this instead of `localStorage.clear()`: a bare clear also destroys the
 * device id and PostHog's identity, which are not the app's session state.
 */
export const clearAppStorage = ({
  keepAnalyticsIdentity = true
}: ClearAppStorageOptions = {}): void => {
  const durableEntries = localStore
    .keys()
    .filter((key) => isDurable(key, keepAnalyticsIdentity))
    .map((key) => [key, localStore.read(key)] as const)
    .filter((entry): entry is readonly [string, string] => entry[1] !== null);

  localStore.clear();

  durableEntries.forEach(([key, value]) => localStore.write(key, value));

  Logger.info(
    `clearAppStorage: reset local storage, kept ${durableEntries.length} durable key(s)`
  );
};
